import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { parseUnits } from 'ethers'
import { AlertTriangle, InfoIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  fetchBalances,
  fetchDelegations,
  fetchNft,
  fetchUnbondingDelegations,
} from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  formatLocalDateTime,
  getNftWithdrawableAtomic,
  isNftFullyUnlocked,
  safeBigInt,
  sumAtomic,
} from '@/helpers'
import { buildWithdrawalMsg, WithdrawalError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const withdrawalSchema = z.object({
  amount: z
    .string()
    .min(1, 'Amount is required')
    .regex(/^\d+(?:\.\d{0,18})?$/, 'Enter a valid amount')
    .refine(value => {
      try {
        return parseUnits(value, 18) > 0n
      } catch {
        return false
      }
    }, 'Enter an amount greater than 0'),
})

type WithdrawalFormValues = z.infer<typeof withdrawalSchema>

type Props = {
  open: boolean
  nftAddress: string
  onClose: () => void
}

function toAtomic(value: string): bigint {
  try {
    return parseUnits(value, 18)
  } catch {
    return 0n
  }
}

export default function WithdrawalModal({ open, nftAddress, onClose }: Props) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()
  const denom = bridgelessChainInfo.stakeCurrency.coinMinimalDenom

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm<WithdrawalFormValues>({
    resolver: zodResolver(withdrawalSchema),
    mode: 'onChange',
    defaultValues: { amount: '' },
  })

  const amountValue = watch('amount')

  const { data: nftData, isLoading: isLoadingNft } = useQuery({
    queryKey: queryKeys.nft.byAddress(nftAddress),
    enabled: open,
    queryFn: async () => fetchNft(nftAddress),
    staleTime: 15_000,
  })

  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: queryKeys.bank.balances(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => fetchBalances(nftAddress),
    staleTime: 15_000,
  })

  const { data: delegationData } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => fetchDelegations(nftAddress),
    staleTime: 30_000,
  })

  // Fetch unbonding delegations to check if funds are still locked
  const { data: unbondingData } = useQuery({
    queryKey: queryKeys.staking.unbondingDelegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => fetchUnbondingDelegations(nftAddress),
    staleTime: 30_000,
  })

  const activeUnbonding = useMemo(() => {
    const unbonding = unbondingData?.unbonding_responses?.[0]
    const entry = unbonding?.entries?.[0]
    if (!entry) return null

    return {
      completionTime: entry.completion_time,
      balance: entry.balance,
    }
  }, [unbondingData])

  const isLoading = isLoadingNft || isLoadingBalances

  const withdrawableAtomic = useMemo(() => {
    if (!nftData || !balancesData) return '0'
    const balanceAtomic = sumAtomic(
      (balancesData.balances ?? [])
        .filter(b => b.denom === denom)
        .map(b => b.amount),
    )
    return getNftWithdrawableAtomic(nftData, balanceAtomic)
  }, [nftData, balancesData, denom])

  const hasAvailable = safeBigInt(withdrawableAtomic) > 0n
  const isDelegated = (delegationData?.delegation_responses ?? []).length > 0
  const isFullyUnlocked = nftData ? isNftFullyUnlocked(nftData) : false

  const amountAtomic = toAtomic(amountValue)
  const exceedsLimit = amountAtomic > safeBigInt(withdrawableAtomic)

  const isWithdrawDisabled = Boolean(activeUnbonding) || isDelegated

  const withdrawTx = useMutation({
    mutationKey: queryKeys.tx.withdraw(nftAddress),
    mutationFn: async (values: WithdrawalFormValues) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      const amount = toAtomic(values.amount)
      if (amount <= 0n || amount > safeBigInt(withdrawableAtomic)) {
        throw new Error('Amount exceeds the available to withdraw')
      }

      const msg = buildWithdrawalMsg({
        creator: address,
        address: nftAddress,
        amount: { denom, amount: amount.toString() },
      })

      const txHash = await signAndBroadcast([msg])
      return txHash
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Withdrawal sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(WithdrawalError.fromUnknown(error))
    },
  })

  const isSubmitting = withdrawTx.isPending

  const submit = handleSubmit(values => withdrawTx.mutate(values))

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Withdraw from Shard</span>}
      content={
        <form
          onSubmit={submit}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <div className='flex items-center justify-between gap-2'>
              <span className='text-muted-foreground text-xs'>
                Amount (BRIDGE)
              </span>
              <span className='text-muted-foreground text-xs'>
                Available:{' '}
                <span className='text-foreground font-semibold'>
                  {isLoading ? '…' : formatBridgeAtomic(withdrawableAtomic)}
                </span>
              </span>
            </div>
            <GradientWrapper>
              <div className='bg-input border-border flex h-12 w-full items-center gap-2 rounded-lg border px-3'>
                <input
                  value={amountValue}
                  disabled={isSubmitting || isLoading || !hasAvailable}
                  onChange={e =>
                    setValue('amount', e.target.value, {
                      shouldValidate: true,
                    })
                  }
                  maxLength={40}
                  className='text-foreground h-full w-full bg-transparent text-sm outline-none'
                  placeholder='0.1'
                />
                <button
                  type='button'
                  className='text-primary shrink-0 text-xs font-semibold disabled:opacity-50'
                  disabled={isSubmitting || isLoading || !hasAvailable}
                  onClick={() =>
                    setValue('amount', formatBridgeAtomic(withdrawableAtomic), {
                      shouldValidate: true,
                    })
                  }
                >
                  Max
                </button>
              </div>
            </GradientWrapper>
            {errors.amount && (
              <span className='text-xs text-red-400'>
                {errors.amount.message as string}
              </span>
            )}
            {!errors.amount && exceedsLimit && (
              <span className='text-xs text-red-400'>
                Amount exceeds the available to withdraw
              </span>
            )}
            {!isLoading && !hasAvailable && (
              <span className='text-xs text-red-400'>
                No amount available to withdraw
              </span>
            )}
          </div>

          {!isLoading && nftData && !isFullyUnlocked && (
            <div className='text-foreground flex items-start gap-2 rounded-md bg-[#141622] p-3 text-xs'>
              <InfoIcon className='mt-0.5 size-4 shrink-0' />
              <span>
                Before the full unlock only the unlocked part can be withdrawn.
                Staking rewards and other transfers to the Shard become
                available after the 100% unlock.
              </span>
            </div>
          )}

          {isDelegated && (
            <div className='flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <AlertTriangle className='size-4 shrink-0 text-yellow-500' />
              <span className='text-xs text-yellow-500'>
                Shard is delegated. Undelegate first to withdraw.
              </span>
            </div>
          )}

          {activeUnbonding && (
            <div className='flex flex-col gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle className='size-4 text-yellow-500' />
                <span className='text-sm font-semibold text-yellow-500'>
                  Unbonding in Progress
                </span>
              </div>
              <p className='text-muted-foreground text-xs'>
                Your funds are currently unbonding and will be available for
                withdrawal on{' '}
                <span className='text-foreground font-semibold'>
                  {formatLocalDateTime(
                    new Date(activeUnbonding.completionTime),
                    'yyyy-MM-dd HH:mm',
                  )}
                </span>{' '}
                (local time).
              </p>
            </div>
          )}
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={
              isSubmitting ||
              isLoading ||
              !hasAvailable ||
              !isValid ||
              exceedsLimit ||
              isWithdrawDisabled
            }
            onClick={submit}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Withdrawing…
              </span>
            ) : (
              'Withdraw'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
